/**
 * Review v0 (Review Flow v2.0, Step 1): the household-wide Review list.
 *
 * The only web layer that touches the batch RPCs and the `review-confirm`
 * edge function:
 * - `getHouseholdReviewItems` — every pending item of the household, across
 *   emails, with its email's subject and sender.
 * - `getOpenTasksForEvents` — the open tasks a cancellation will cancel.
 * - `setAsideReviewItem` — Don't change / Keep event / Don't add: one item
 *   off the list (a new event's with its pending tasks), through the soft
 *   `dismiss_review_item` RPC (undoable).
 * - `confirmReviewBatch` — Confirm all, through `review-confirm`.
 * - `undoReviewBatch` — reverts a batch inside the undo window.
 * - `getReviewItemSources` — an item's source emails and Smart Summary for
 *   the details sheet.
 *
 * Today's per-email Review keeps using email.service; nothing here changes
 * it.
 */
import { supabase, invokeFunction } from '@/lib/supabase';
import { addBreadcrumb } from '@/lib/sentry';
import { parseInvokeErrorBody } from '@/lib/invoke-error';
import type { OpenScheduleTask } from '@/pages/review/reviewListModel';
import type { Email, ReviewItem } from '@/types';
import {
  QuotaExceededError,
  getEmail,
  getExtraction,
  getProposalTargets,
  toDismissResult,
  type EmbeddedSource,
  type ProposalTarget,
} from './email.service';

// Confirm all throws it; re-exported so callers read every Review error here.
export { QuotaExceededError };

/** A task's related review item (related_event_id), as embedded on the list read. */
export type RelatedReviewItem = Pick<
  ReviewItem,
  | 'id'
  | 'status'
  | 'type'
  | 'update_hint'
  | 'proposed_action'
  | 'supersedes_event_id'
  | 'supersedes_task_id'
>;

export type HouseholdReviewItem = ReviewItem & {
  email: {
    id: string;
    subject: string | null;
    from_email: string;
    sender_label: string | null;
  };
  /** Null when the row has no related_event_id or the related row is gone. */
  related: RelatedReviewItem | null;
};

/**
 * Every pending item of the household, oldest first, with its email's
 * subject and sender, and the review item it is related to (a task's
 * event, whatever that item's status — the list badges the task with it).
 *
 * Only items of emails still in REVIEW_REQUIRED: a pending row can survive
 * on a PROCESSED email in edge cases, and the list must never show it
 * (confirming it would be refused as stale). The household is bound
 * explicitly — RLS admits every household of the user, not the active one.
 */
export async function getHouseholdReviewItems(
  householdId: string,
): Promise<HouseholdReviewItem[]> {
  // `related:related_event_id(…)` is the parent row (many-to-one). On a
  // self-referencing FK PostgREST rejects the constraint-name hint (PGRST200)
  // and `review_items!related_event_id` embeds the children instead.
  const { data, error } = await supabase
    .from('review_items')
    .select(
      '*, email:emails!email_id!inner(id, subject, from_email, sender_label, status), related:related_event_id(id, status, type, update_hint, proposed_action, supersedes_event_id, supersedes_task_id)',
    )
    .eq('household_id', householdId)
    .eq('status', 'pending')
    .eq('email.status', 'REVIEW_REQUIRED')
    .order('created_at', { ascending: true });

  if (error) throw error;
  const items: HouseholdReviewItem[] = (data ?? []).map((row) => {
    const { email, related, ...item } = row;
    return {
      ...item,
      email: {
        id: email.id,
        subject: email.subject,
        from_email: email.from_email,
        sender_label: email.sender_label,
      },
      related: related ?? null,
    };
  });
  const ids = new Set(items.map((i) => i.id));
  addBreadcrumb('review_items.household_fetched', {
    count: items.length,
    emails: new Set(items.map((i) => i.email.id)).size,
    related_off_list: items.filter((i) => i.related && !ids.has(i.related.id))
      .length,
  });
  return items;
}

/**
 * Open tasks (TODO / IN_PROGRESS) of the given events, by event id — exactly
 * what apply_review_email's cancel cascade cancels (20261026171916:270-286).
 * Household bound explicitly (RLS admits every household of the user).
 */
export async function getOpenTasksForEvents(
  eventIds: readonly string[],
  householdId: string,
): Promise<Map<string, OpenScheduleTask[]>> {
  const byEvent = new Map<string, OpenScheduleTask[]>();
  if (eventIds.length === 0) return byEvent;
  const { data, error } = await supabase
    .from('tasks')
    .select(
      'id, title, due_date, related_event_id, source_review_item:review_items!source_review_item_id(is_time_unknown)',
    )
    .eq('household_id', householdId)
    .in('related_event_id', [...eventIds])
    .in('status', ['TODO', 'IN_PROGRESS'])
    .order('due_date', { ascending: true, nullsFirst: false });
  if (error) throw error;

  let count = 0;
  for (const t of data ?? []) {
    if (!t.related_event_id) continue;
    const source = t.source_review_item as EmbeddedSource;
    const list = byEvent.get(t.related_event_id) ?? [];
    list.push({
      id: t.id,
      title: t.title,
      due: t.due_date,
      isTimeUnknown: source?.is_time_unknown ?? false,
    });
    byEvent.set(t.related_event_id, list);
    count += 1;
  }
  addBreadcrumb('review_items.cancel_tasks_fetched', {
    events: eventIds.length,
    tasks: count,
  });
  return byEvent;
}

/**
 * The household's pending items plus the schedule items any of them propose
 * to update or cancel, and the open tasks of each event a cancellation
 * removes. A task's related update item that is not on the list (set
 * aside, committed, or on a closed email) has its target read with the
 * others, so the task keeps its badge. A failed target or tasks read keeps
 * the items and reports `targetsFailed`, so the card does not pretend a
 * hinted item is new.
 */
export async function getHouseholdReviewItemsWithTargets(
  householdId: string,
): Promise<{
  items: HouseholdReviewItem[];
  targets: Map<string, ProposalTarget>;
  targetsFailed: boolean;
  openTasks: Map<string, OpenScheduleTask[]>;
}> {
  const items = await getHouseholdReviewItems(householdId);
  try {
    const pendingIds = new Set(items.map((i) => i.id));
    const offList = items.flatMap((i) =>
      i.related && !pendingIds.has(i.related.id) ? [i.related] : [],
    );
    const targets = await getProposalTargets(
      [...items, ...offList],
      householdId,
    );
    // Only a cancel of an event that was read cascades to its tasks.
    const cancelledEventIds = new Set<string>();
    for (const item of items) {
      if (
        item.update_hint &&
        item.proposed_action === 'cancel' &&
        item.supersedes_event_id &&
        targets.has(item.supersedes_event_id)
      ) {
        cancelledEventIds.add(item.supersedes_event_id);
      }
    }
    const openTasks = await getOpenTasksForEvents(
      [...cancelledEventIds],
      householdId,
    );
    return { items, targets, targetsFailed: false, openTasks };
  } catch {
    return {
      items,
      targets: new Map(),
      targetsFailed: true,
      openTasks: new Map(),
    };
  }
}

export type SetAsideResult = {
  /** The batch the set-aside was recorded under — what Undo reverts. */
  batchId: string | null;
  /** Set when this took the email's last pending item (email now PROCESSED). */
  emailDisposition: 'APPROVED' | 'DISMISSED' | null;
};

/**
 * Don't change / Keep event / Don't add → the `dismiss_review_item` RPC
 * (soft dismiss, recorded as a review batch, undoable). The caller decides
 * whether an event's pending tasks go with it. The RPC authorizes
 * the caller itself. A `batchId` of null means the item was no longer
 * pending and nothing was written.
 */
export async function setAsideReviewItem(
  itemId: string,
  opts: {
    /** A new event's Don't add takes its pending tasks (one Undo brings all back). */
    includeRelatedTasks: boolean;
  },
): Promise<SetAsideResult> {
  const { data, error } = await supabase.rpc('dismiss_review_item', {
    p_review_item_id: itemId,
    // Taken tasks share the item's batch. The old per-email screen's
    // deleteReviewItem keeps the single-item default.
    p_include_related_tasks: opts.includeRelatedTasks,
  });
  if (error) throw error;
  const { batchId, emailDisposition } = toDismissResult(data);
  addBreadcrumb('review_item.set_aside', {
    review_item_id: itemId,
    batch_id: batchId,
    email_disposition: emailDisposition,
  });
  return { batchId, emailDisposition };
}

export interface ConfirmBatchInput {
  /**
   * review_item ids of every card the parent saw (folded tasks included).
   * Only these are confirmed; reuse the same ids on every retry of a series.
   */
  itemIds: readonly string[];
  child_id?: string;
  /** Per-item household-member assignments: review_item_id → user ids. */
  item_member_ids?: Record<string, string[]>;
}

/**
 * What `confirm_review_batch` returns, as `review-confirm` relays it.
 * `batch_id` is null (and every count 0) when nothing was pending.
 */
export type ConfirmBatchRpcResult = {
  batch_id: string | null;
  emails_confirmed: number;
  items_confirmed: number;
  tasks_created: number;
  events_created: number;
  tasks_updated: number;
  events_updated: number;
  tasks_cancelled: number;
  events_cancelled: number;
  cancels_skipped: number;
  series_created: number;
  occurrences_created: number;
};

export type ConfirmBatchResult = ConfirmBatchRpcResult & { success: true };

/** The list was out of date (409 STALE_ITEMS): nothing was written. */
export class StaleReviewListError extends Error {
  readonly code = 'STALE_ITEMS' as const;
  constructor() {
    super('The review list changed. Refresh and try again.');
    this.name = 'StaleReviewListError';
  }
}

/** Undo was refused because the batch's undo window has closed. */
export class UndoWindowClosedError extends Error {
  readonly code = 'undo_window_closed' as const;
  constructor() {
    super('undo_window_closed');
    this.name = 'UndoWindowClosedError';
  }
}

function numberField(data: object, key: string): number {
  const value: unknown = (data as Record<string, unknown>)[key];
  return typeof value === 'number' ? value : 0;
}

function toConfirmResult(data: unknown): ConfirmBatchResult {
  if (
    typeof data !== 'object' ||
    data === null ||
    !('success' in data) ||
    data.success !== true
  ) {
    throw new Error('review-confirm did not report success');
  }
  const batchId =
    'batch_id' in data && typeof data.batch_id === 'string'
      ? data.batch_id
      : null;
  return {
    success: true,
    batch_id: batchId,
    emails_confirmed: numberField(data, 'emails_confirmed'),
    items_confirmed: numberField(data, 'items_confirmed'),
    tasks_created: numberField(data, 'tasks_created'),
    events_created: numberField(data, 'events_created'),
    tasks_updated: numberField(data, 'tasks_updated'),
    events_updated: numberField(data, 'events_updated'),
    tasks_cancelled: numberField(data, 'tasks_cancelled'),
    events_cancelled: numberField(data, 'events_cancelled'),
    cancels_skipped: numberField(data, 'cancels_skipped'),
    series_created: numberField(data, 'series_created'),
    occurrences_created: numberField(data, 'occurrences_created'),
  };
}

/**
 * Confirm all: `review-confirm` applies the listed pending items of the
 * household (`input.itemIds`, what the parent saw) as one batch. `idempotencyKey` is one uuid per attempt series — reuse it
 * on a retry so a request that did commit returns the same batch.
 *
 * Throws QuotaExceededError (403 QUOTA_EXCEEDED), StaleReviewListError
 * (409 STALE_ITEMS — refetch, do not retry), or the invoke error.
 */
export async function confirmReviewBatch(
  householdId: string,
  idempotencyKey: string,
  input: ConfirmBatchInput,
): Promise<ConfirmBatchResult> {
  addBreadcrumb('review.confirm_started', {
    household_id: householdId,
    items_count: input.itemIds.length,
    has_child_id: !!input.child_id,
    has_item_member_ids: !!input.item_member_ids,
  });
  const { data, error } = await invokeFunction('review-confirm', {
    body: {
      household_id: householdId,
      idempotency_key: idempotencyKey,
      item_ids: input.itemIds,
      child_id: input.child_id,
      item_member_ids: input.item_member_ids,
    },
  });

  if (error) {
    const body = await parseInvokeErrorBody(error);
    if (body && body.code === 'QUOTA_EXCEEDED') {
      const { tier, used, quota } = body;
      if (
        (tier === 'FREE' || tier === 'CORE' || tier === 'FAMILY') &&
        typeof used === 'number' &&
        typeof quota === 'number'
      ) {
        addBreadcrumb('review.confirm_quota_exceeded', { tier, used, quota });
        throw new QuotaExceededError(tier, used, quota);
      }
    }
    if (body && body.code === 'STALE_ITEMS') {
      addBreadcrumb('review.confirm_stale', { household_id: householdId });
      throw new StaleReviewListError();
    }
    addBreadcrumb('review.confirm_failed', { household_id: householdId });
    throw error;
  }

  const result = toConfirmResult(data);
  addBreadcrumb('review.confirm_succeeded', {
    batch_id: result.batch_id,
    items_confirmed: result.items_confirmed,
    emails_confirmed: result.emails_confirmed,
  });
  return result;
}

export type UndoBatchResult = {
  undone: true;
  /** Review items put back to pending. */
  itemsRestored: number;
  /**
   * Rows the revert left as they are because they were edited after the
   * batch (or are gone). Mixes units — kept review items AND kept tasks of a
   * cancelled event — so it is NOT a count of "items kept"; use it only as
   * "something was skipped" (> 0).
   */
  itemsSkipped: number;
};

/**
 * Revert a batch (confirm or set-aside) inside the undo window, through the
 * `undo_review_batch` RPC. Throws UndoWindowClosedError when the server
 * refuses because the window has closed; any other refusal
 * (`batch_not_found`, `not_authorized`, `batch_not_applied`) is thrown as
 * the RPC error.
 */
export async function undoReviewBatch(
  batchId: string,
): Promise<UndoBatchResult> {
  const { data, error } = await supabase.rpc('undo_review_batch', {
    p_batch_id: batchId,
  });
  if (error) {
    if (error.message?.includes('undo_window_closed')) {
      addBreadcrumb('review.undo_window_closed', { batch_id: batchId });
      throw new UndoWindowClosedError();
    }
    addBreadcrumb('review.undo_failed', { batch_id: batchId });
    throw error;
  }
  const obj = typeof data === 'object' && data !== null ? data : {};
  const result: UndoBatchResult = {
    undone: true,
    itemsRestored: numberField(obj, 'items_restored'),
    itemsSkipped: numberField(obj, 'items_skipped'),
  };
  addBreadcrumb('review.undo_succeeded', {
    batch_id: batchId,
    items_restored: result.itemsRestored,
    items_skipped: result.itemsSkipped,
  });
  return result;
}

export interface ReviewItemSources {
  /** In the order asked (newest first); an email that cannot be read is dropped. */
  emails: Email[];
  /** The newest email's extraction summary (R2); null when there is none. */
  summary: string | null;
}

/**
 * The emails an item came from and the newest one's Smart Summary, for the
 * details sheet's Sources (YAR-551 D2). An email that reads as missing
 * (deleted by retention, or of a household the user left) is dropped and
 * the count follows; a failed read throws so the sheet can offer a retry.
 * A failed summary read only hides the summary.
 */
export async function getReviewItemSources(
  emailIds: readonly string[],
): Promise<ReviewItemSources> {
  const [emails, extraction] = await Promise.all([
    Promise.all(emailIds.map((id) => getEmail(id))),
    emailIds.length > 0
      ? getExtraction(emailIds[0]).catch(() => null)
      : Promise.resolve(null),
  ]);
  const found = emails.filter((email): email is Email => email !== null);
  const summary = extraction?.summary?.trim() || null;
  addBreadcrumb('review_item.sources_fetched', {
    requested: emailIds.length,
    found: found.length,
    hasSummary: summary !== null,
  });
  return { emails: found, summary };
}
